import React, { useCallback } from 'react';
import { FieldType, FieldOption } from 'types/FieldTypes';
import FormModal from 'components/modals/FormModal';
import { useSettingsDivision } from 'components/settings/SettingsDivisionContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useActiveFeature, useAllowedRight } from 'services/permissions/permissionChecks';
import { useSubcontractorConversion } from 'services/subcontractors/useSubcontractorConversion';
import DropdownButton from 'components/buttons/DropdownButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { UserType } from './UserTypes';
import { SeatUsageType, getRoleLicensePool } from 'services/users/licensePools';

interface UserFormProps {
    viewKey?: string;
    itemId?: number;
    user?: UserType; // The list row of the edited user, used for the subcontractor conversion button
    seatUsage?: SeatUsageType | null; // The seat usage of the user list, limits the roles of a new user to the free licenses
}

// Rename the name of the function:
const UserForm: React.FC<UserFormProps> = ({ viewKey, itemId, user, seatUsage }) => {

    // New users may only get a role of a license pool which still has room
    const availablePools = (!itemId && seatUsage && !seatUsage.has_unlimited_seats && seatUsage.available_license_pools) || null;
    const availablePoolsKey = availablePools ? availablePools.join(',') : '';
    const roleOptionFilter = useCallback((role: FieldOption) => {
        if (!availablePools || !seatUsage) return true;
        const pool = getRoleLicensePool(role as unknown as { access_to?: string[]; license_type?: string; is_subcontractor_role?: boolean }, seatUsage);
        return pool === null || availablePools.includes(pool);
    }, [availablePoolsKey]);

    // Prefill the division with the division selected in the settings menu
    const { selectedDivision, isEnvironmentSelected } = useSettingsDivision();
    const divisionDefaultValue = !isEnvironmentSelected && selectedDivision ? { value: String(selectedDivision.id) } : undefined;

    // Only superusers can mark users of the admin environment as staff user, the platform employees
    const { environmentType } = useAuthContext();
    const isSuperuser = useAllowedRight('no_user');
    const showStaffField = isSuperuser && environmentType === 'admin';

    const apiObject = 'user'

    // Only existing users with a role which gives field access only can become a subcontractor
    const subcontractorsActive = useActiveFeature('subcontractors');
    const { convertUserToSubcontractor } = useSubcontractorConversion();
    const canConvertToSubcontractor = Boolean(
        subcontractorsActive && itemId && user && user.field_access && !user.office_access &&
        !user.is_superuser && !user.is_staff && !user.is_subcontractor_role
    );

    // Plug in the fields and their properties:
    const fields: FieldType[] = [
        {
            name: 'first_name',
            label: 'user.general.first_name_label',
            type: 'text',
            placeholder: 'user.general.first_name_placeholder',
            width: '50%',
            group: 'field-row',
        },
        {
            name: 'last_name',
            label: 'user.general.last_name_label',
            type: 'text',
            placeholder: 'user.general.last_name_placeholder',
            width: '50%',
            group: 'field-row',
        },
        {
            name: 'email',
            label: 'user.general.email_label',
            type: 'email',
            placeholder: 'user.general.email_placeholder',
            helperText: 'user.general.email_helperText',
        },
        {
            name: 'password',
            label: 'user.general.password_label',
            type: 'password',
            placeholder: 'user.general.password_placeholder',
        },
        {
            name: 'role',
            label: 'user.general.role_label',
            type: 'dropdown',
            disabled_selected: 'user.general.role_disabled_selected',
            showSearch: false,
            apiRequest: [{
                endpoint: 'get_role_list',
                object: 'role',
                params: { is_active: true },
                useCache: true
            }],
            allowNoneOption: false,
            optionFilter: availablePools ? roleOptionFilter : undefined,
            helperText: availablePools && !availablePools.includes('full') ? 'user.general.role_light_only_helperText' : 'user.general.role_helperText'
        },
        {
            name: 'division',
            defaultValue: divisionDefaultValue,
            label: 'user.general.division_label',
            type: 'dropdown',
            disabled_selected: 'user.general.select_division_disabled_selected',
            showSearch: false,
            apiRequest: [{
                endpoint: 'get_division_list',
                object: 'division',
                params: { is_active: true },
                useCache: true
            }],
            allowNoneOption: true,
            activeFeature: 'divisions',
            helperText: 'user.general.division_helperText'
        },
        {
            name: 'profile_picture',
            label: 'user.general.profile_picture_label',
            type: 'image',
            imageType: 'smallCircle',
            dropzoneText: 'user.general.profile_picture_placeholder',
            apiField: { 
                fileUpload: 'profile_picture_file', 
                fileName: 'profile_picture_filename',
                imageDeleted: 'profile_picture_deleted'
            },
            cropConfig: {
                aspectRatio: 1,
                cropShape: 'round',
                resize: { width: 200, height: 200 }
            }
        },
        {
            name: 'external_code',
            label: 'user.general.external_code_label',
            type: 'text',
            placeholder: 'user.general.external_code_placeholder',
            tooltipText: 'user.general.external_code_tooltipText'
        },
        {
            name: 'is_active',
            label: 'user.general.is_active_label',
            type: 'checkbox',
            showOnEdit: true,
            tooltipText: 'user.general.is_active_tooltipText',
        },
        // Only superusers in the admin environment see the staff user checkbox
        ...(showStaffField ? [{
            name: 'is_staff',
            label: 'user.general.is_staff_label',
            type: 'checkbox',
            tooltipText: 'user.general.is_staff_tooltipText',
        } as FieldType] : []),
    ];
    
    return (
        <FormModal 
            viewKey={viewKey}
            apiObject={apiObject}
            fields={fields}
            extraLeftButtons={canConvertToSubcontractor ? (
                <DropdownButton
                    icon={<FontAwesomeIcon icon={faEllipsis} />}
                    tooltipText='general.other'
                    items={[{ label: 'subcontractor.conversion.to_subcontractor_button', onClick: () => convertUserToSubcontractor(itemId as number) }]}
                    size='small'
                    menuDirection='up'
                    onlyViewRestriction={true}
                />
            ) : undefined}
        />
    );
};

export default UserForm;